import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  buildSearchDestination,
  type SearchDestination,
} from "@/features/catalog/utils/catalogSearch";
import type { SearchScope } from "@/features/search/searchScope";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type CatalogSearchState = {
  readonly query: string;
  readonly scope: SearchScope;
};

export type CatalogSearchEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/search-query-changed"
      | "catalog/search-cleared"
      | "catalog/search-scope-changed"
      | "catalog/search-submitted"
      | "catalog/search-synchronized";
  }
>;

export function isCatalogSearchEvent(
  event: CatalogEvent,
): event is CatalogSearchEvent {
  return (
    event.type === "catalog/search-query-changed" ||
    event.type === "catalog/search-cleared" ||
    event.type === "catalog/search-scope-changed" ||
    event.type === "catalog/search-submitted" ||
    event.type === "catalog/search-synchronized"
  );
}

export type CatalogSearchEffect =
  | { readonly type: "set-query"; readonly query: string }
  | { readonly type: "set-scope"; readonly scope: SearchScope }
  | { readonly type: "navigate"; readonly destination: SearchDestination };

export function createCatalogSearchMediator(
  initialState: CatalogSearchState,
  runEffect: (effect: CatalogSearchEffect) => void,
  restoredState?: CatalogSearchState,
): Mediator<CatalogSearchState, CatalogSearchEvent, CatalogSearchEffect> {
  return createMediator(
    restoredState ?? initialState,
    (state, event) => {
      switch (event.type) {
        case "catalog/search-query-changed":
          return {
            status: "handled",
            state: { ...state, query: event.query },
            effects: [{ type: "set-query", query: event.query }],
          };
        case "catalog/search-cleared":
          return {
            status: "handled",
            state: { ...state, query: "" },
            effects: [{ type: "set-query", query: "" }],
          };
        case "catalog/search-scope-changed":
          return {
            status: "handled",
            state: { ...state, scope: event.scope },
            effects: [{ type: "set-scope", scope: event.scope }],
          };
        case "catalog/search-submitted": {
          const destination = buildSearchDestination(state.query, state.scope);
          return destination
            ? {
                status: "handled",
                state,
                effects: [{ type: "navigate", destination }],
              }
            : { status: "handled", state };
        }
        case "catalog/search-synchronized":
          return {
            status: "handled",
            state: { query: event.query, scope: event.scope },
          };
      }
    },
    { runEffect },
  );
}
